import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, GraduationCap, Radio, Compass, Trophy, Flame } from "lucide-react";
import { http } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";

const KIND = {
  levelup: { icon: Award, color: "bg-[#F2D49B] text-[#5B3A1E]", label: "Level-Ups" },
  checkin: { icon: Compass, color: "bg-[#C8DCCB] text-[#2C4A3E]", label: "Check-ins" },
  teach: { icon: GraduationCap, color: "bg-[#F6C6A8] text-[#7A3E1D]", label: "Teach My Buddy" },
  live: { icon: Radio, color: "bg-[#D9D4E7] text-[#3E3560]", label: "Live classes" },
  streak: { icon: Flame, color: "bg-[#F6C6A8] text-[#8A3A12]", label: "Goal Streaks" },
  goal: { icon: Trophy, color: "bg-[#FBE3B5] text-[#7A4A12]", label: "Family Goals" },
};

const Badge = ({ b }) => {
  const k = KIND[b.kind];
  const body = (
    <div className="group w-28 shrink-0 text-center" data-testid={`badge-${b.id}`}>
      <div className={`mx-auto w-16 h-16 rounded-full grid place-items-center shadow-[0_6px_0_rgba(0,0,0,0.08)] transition-transform group-hover:-translate-y-1 group-hover:rotate-3 ${k.color}`}><k.icon className="w-7 h-7" /></div>
      <p className="mt-2 text-xs font-semibold text-[#2C4A3E] leading-tight">{b.name}</p>
      <p className="text-[10px] text-stone-500 leading-tight line-clamp-2">{b.note}</p>
    </div>
  );
  return b.link ? <Link to={b.link}>{body}</Link> : body;
};

export const BadgeShelf = ({ studentId, compact = false }) => {
  const { buddy, profile } = useProfile();
  const [list, setList] = useState(null);
  useEffect(() => { http.get(studentId ? `/students/${studentId}/badges` : "/badges/me").then((r) => setList(r.data)).catch(() => setList([])); }, [studentId]);
  if (!list) return null;
  const pin = (b) => (b.kind === "streak" || b.kind === "goal" ? 0 : 1);
  const shown = compact ? [...list].sort((a, b) => pin(a) - pin(b)).slice(0, 8) : list;
  const mine = !studentId;
  return (
    <section className="mt-12" data-testid="badge-shelf">
      <div className="flex items-end gap-4">
        {mine && buddy && <div className="w-16 h-16 shrink-0 float-up"><BuddyAvatar buddy={buddy} className="w-full h-full" /></div>}
        <div className="flex-1">
          <h2 className="font-display text-2xl text-[#2C4A3E]">{mine ? `${buddy?.name || "Your Buddy"}'s trophy shelf for ${profile?.name?.split(" ")[0]}` : "Badge shelf"}</h2>
          <p className="text-sm text-stone-500" data-testid="badge-count">{list.length} badge{list.length === 1 ? "" : "s"} earned{mine && list.length > 0 ? ". Look at everything you've done!" : ""}</p>
        </div>
        {mine && compact && list.length > 8 && <Link to="/buddy" className="text-sm text-[#C87D53]" data-testid="badge-shelf-more">See all</Link>}
      </div>
      <div className="mt-5 rounded-3xl bg-gradient-to-b from-[#FBF3E6] to-[#F1EADB] border border-[#E6DEC3] px-5 pt-6 pb-0 overflow-hidden">
        {shown.length ? (
          <div className={`flex gap-3 pb-4 ${compact ? "overflow-x-auto" : "flex-wrap gap-y-6"}`}>{shown.map((b) => <Badge key={b.id} b={b} />)}</div>
        ) : <p className="text-sm text-stone-500 pb-6" data-testid="badge-empty">Badges will sit here as you finish check-ins, level up, teach your Buddy and join live classes.</p>}
        <div className="h-3 -mx-5 bg-[#C9A27E] shadow-[inset_0_-3px_0_rgba(0,0,0,0.15)]" />
      </div>
    </section>
  );
};
